import { Eyebrow } from './Section.jsx';

function dayLabel(startTime, index) {
  if (index === 0) return 'Today';
  try {
    return new Date(startTime).toLocaleDateString('en-US', { weekday: 'short' });
  } catch {
    return '';
  }
}

// NWS shortForecast strings ("Sunny then Slight Chance Showers And
// Thunderstorms") don't fit a five-column card and were clipping mid-phrase.
// Map them to a short editorial vocabulary; anything unrecognized falls back
// to the sky term alone so a raw feed string never renders.
function precipLabel(text) {
  const storms = /thunderstorm/i.test(text);
  const showers = /shower|rain|drizzle/i.test(text);
  if (!storms && !showers) return null;
  const kind = storms ? 'storms' : 'showers';
  if (/slight chance|chance|possible|isolated|scattered/i.test(text)) return `${kind} possible`;
  if (/likely/i.test(text)) return `${kind} likely`;
  return kind;
}

function skyLabel(text) {
  if (/mostly sunny|mostly clear/i.test(text)) return 'Mostly sunny';
  if (/partly sunny|partly cloudy/i.test(text)) return 'Part sun';
  if (/mostly cloudy|cloudy|overcast/i.test(text)) return 'Clouds';
  if (/sunny|clear/i.test(text)) return 'Sunny';
  if (/fog/i.test(text)) return 'Fog';
  return null;
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Clean, legible weather glyphs drawn from the same forecast text. The raw NWS
// icons are busy split-panel rasters with tiny embedded percentages that turn
// to mush at this size, so we render our own in the almanac palette instead.
const GOLD = '#cf9844'; // cream-500 — sun, lightning
const CLOUD = '#8eaaca'; // navy-300 — fair-weather cloud
const CLOUD_DARK = '#5d83ad'; // navy-400 — rain/storm cloud
const DROP = '#3f6694'; // navy-500 — raindrops

// Pick one glyph from the forecast string: a base sky plus optional precip,
// and whether the sun should still peek through (a clear/partly first half).
function iconKind(shortForecast) {
  const text = shortForecast || '';
  const first = text.split(/\s+then\s+/i)[0];
  const storms = /thunderstorm/i.test(text);
  const showers = !storms && /shower|rain|drizzle/i.test(text);
  const sunny =
    /sunny|clear|mostly sunny|partly|few|hazy/i.test(first) &&
    !/mostly cloudy|overcast/i.test(first);

  if (storms) return sunny ? 'sun-storms' : 'storms';
  if (showers) return sunny ? 'sun-showers' : 'showers';
  if (/fog|haze/i.test(first)) return 'fog';
  if (/mostly cloudy|overcast|^cloudy|^clouds/i.test(first)) return 'cloudy';
  if (/partly|mostly sunny|few/i.test(first)) return 'partly';
  return 'sun';
}

function Sun({ cx, cy, r, rays = true }) {
  const angles = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <g>
      {rays &&
        angles.map((a) => {
          const rad = (a * Math.PI) / 180;
          return (
            <line
              key={a}
              x1={cx + Math.cos(rad) * (r + 1.3)}
              y1={cy + Math.sin(rad) * (r + 1.3)}
              x2={cx + Math.cos(rad) * (r + 3)}
              y2={cy + Math.sin(rad) * (r + 3)}
              stroke={GOLD}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          );
        })}
      <circle cx={cx} cy={cy} r={r} fill={GOLD} />
    </g>
  );
}

function Cloud({ fill, transform }) {
  return (
    <path
      transform={transform}
      d="M6.5 19a4.5 4.5 0 0 1-.5-8.97 6 6 0 0 1 11.64-1.05A4 4 0 0 1 18 19H6.5z"
      fill={fill}
    />
  );
}

function Drops() {
  return (
    <g stroke={DROP} strokeWidth="1.6" strokeLinecap="round">
      <line x1="9" y1="19.5" x2="8" y2="22.5" />
      <line x1="13" y1="19.5" x2="12" y2="22.5" />
      <line x1="17" y1="19.5" x2="16" y2="22.5" />
    </g>
  );
}

function Bolt() {
  return <path d="M13 18.5l-3.4 4.4h2.3l-1 2.6 3.9-5.1h-2.3l1.4-1.9z" fill={GOLD} />;
}

function WeatherIcon({ kind, className }) {
  const combo = kind.startsWith('sun-');
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      role="img"
      aria-hidden="true"
      fill="none"
    >
      {kind === 'sun' && <Sun cx={12} cy={12} r={5} />}
      {kind === 'partly' && (
        <>
          <Sun cx={8} cy={8} r={3.2} />
          <Cloud fill={CLOUD} transform="translate(2.5 3.5) scale(0.82)" />
        </>
      )}
      {kind === 'cloudy' && <Cloud fill={CLOUD} />}
      {kind === 'fog' && (
        <>
          <Cloud fill={CLOUD} />
          <g stroke={CLOUD_DARK} strokeWidth="1.5" strokeLinecap="round">
            <line x1="6" y1="21" x2="16" y2="21" />
            <line x1="8" y1="23.5" x2="18" y2="23.5" />
          </g>
        </>
      )}
      {kind === 'showers' && (
        <>
          <Cloud fill={CLOUD_DARK} />
          <Drops />
        </>
      )}
      {kind === 'storms' && (
        <>
          <Cloud fill={CLOUD_DARK} />
          <Bolt />
        </>
      )}
      {combo && (
        <>
          <Sun cx={8} cy={8} r={3.2} />
          <Cloud fill={CLOUD_DARK} transform="translate(2.5 3.5) scale(0.82)" />
          {kind === 'sun-showers' && <Drops />}
          {kind === 'sun-storms' && <Bolt />}
        </>
      )}
    </svg>
  );
}

export function forecastLabel(shortForecast) {
  if (!shortForecast) return '';
  const [first, second] = shortForecast.split(/\s+then\s+/i);
  const firstPrecip = precipLabel(first);
  const laterPrecip = second ? precipLabel(second) : null;

  // Precip somewhere in the period wins the label; "then <precip>" reads as
  // later-in-day, so prefix PM.
  if (firstPrecip && laterPrecip) {
    return cap(/storms/.test(laterPrecip) ? laterPrecip : firstPrecip);
  }
  if (firstPrecip) return cap(firstPrecip);
  const sky = skyLabel(first) || cap(first.toLowerCase());
  if (laterPrecip) {
    return `${sky}, PM ${/storms/.test(laterPrecip) ? 'storms' : 'showers'}`;
  }
  return sky;
}

export default function ForecastStrip({ forecast }) {
  if (!forecast?.length) return null;
  // Rendered inside the Weather section now, so it carries a lightweight gold
  // kicker rather than its own full masthead.
  return (
    <div className="mt-6">
      <Eyebrow text="Next 5 days" />
      <div className="grid grid-cols-5 gap-2 text-center sm:gap-3">
        {forecast.map((day, i) => (
          <div key={day.startTime || i} className="rounded-lg bg-cream-100 px-1.5 py-3 sm:px-2">
            <div className="text-xs font-medium uppercase tracking-wide text-navy-500">
              {dayLabel(day.startTime, i)}
            </div>
            <WeatherIcon
              kind={iconKind(day.shortForecast)}
              className="mx-auto my-1.5 h-10 w-10"
            />
            <div className="font-display text-sm font-semibold text-navy-900 sm:text-base">
              {day.high}°{day.low != null ? ` / ${day.low}°` : ''}
            </div>
            {/* line-clamp-3: "Part sun, PM showers" wraps to three lines in a
                phone-width cell and was clipping to "Part sun, PM…". */}
            <div className="mt-1 line-clamp-3 text-[11px] leading-tight text-navy-600 sm:text-xs">
              {forecastLabel(day.shortForecast)}
            </div>
            {day.pop != null && day.pop >= 20 && (
              <div className="mt-1 text-[11px] font-medium text-charlestonpink-600">
                {day.pop}% rain
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
